import {
  CategoryStandardProduct,
  StandardProductCategory,
} from "shared/api/middleware-api-client";

type SelectionSource = "standard-product" | "selectedCategory" | "default";

interface SelectionResolution {
  selectedCategory: StandardProductCategory | undefined;
  selectedSample: CategoryStandardProduct | undefined;
  source: SelectionSource;
}

interface ResolveSelectionInput {
  sampleCategories: StandardProductCategory[];
  standardProductId: string | null;
  selectedCategoryParam: string | null;
}

const normalizeCategoryCode = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "")
    .replace(/^kleurstaal/, "");

const findByStandardProductId = (
  sampleCategories: StandardProductCategory[],
  standardProductId: string,
): SelectionResolution | undefined => {
  for (const category of sampleCategories) {
    const matchingProduct = category.product.find(
      (product) => product.standardProduct.id === standardProductId,
    );

    if (matchingProduct) {
      return {
        selectedCategory: category,
        selectedSample: matchingProduct.standardProduct,
        source: "standard-product",
      };
    }
  }

  return undefined;
};

const findByCategoryParam = (
  sampleCategories: StandardProductCategory[],
  selectedCategoryParam: string,
): SelectionResolution | undefined => {
  const normalizedSelectedCategory = normalizeCategoryCode(
    selectedCategoryParam,
  );
  const matchingCategory = sampleCategories.find(
    (category) =>
      normalizeCategoryCode(category.code) === normalizedSelectedCategory,
  );

  if (!matchingCategory) {
    return undefined;
  }

  return {
    selectedCategory: matchingCategory,
    selectedSample: matchingCategory.product[0]?.standardProduct,
    source: "selectedCategory",
  };
};

export const resolveInitialSampleSelection = ({
  sampleCategories,
  standardProductId,
  selectedCategoryParam,
}: ResolveSelectionInput): SelectionResolution => {
  if (sampleCategories.length === 0) {
    return {
      selectedCategory: undefined,
      selectedSample: undefined,
      source: "default",
    };
  }

  if (standardProductId) {
    const byProductId = findByStandardProductId(
      sampleCategories,
      standardProductId,
    );

    if (byProductId) {
      return byProductId;
    }
  }

  if (selectedCategoryParam) {
    const byCategory = findByCategoryParam(
      sampleCategories,
      selectedCategoryParam,
    );

    if (byCategory) {
      return byCategory;
    }
  }

  const defaultCategory = sampleCategories[0];
  return {
    selectedCategory: defaultCategory,
    selectedSample: defaultCategory.product[0]?.standardProduct,
    source: "default",
  };
};

export type { SelectionResolution, SelectionSource };
